import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { ThemeMode, createTheme } from '../theme/theme'

interface ThemeContextType {
  themeMode: ThemeMode
  actualTheme: 'light' | 'dark'
  setThemeMode: (mode: ThemeMode) => void
  theme: ReturnType<typeof createTheme>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'dark' // Default fallback
}

const getStoredTheme = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('theme-mode')
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode
    }
  }
  return 'system' // Default to system preference
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredTheme)
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystemTheme)

  // Determine the actual theme to use
  const actualTheme = themeMode === 'system' ? systemTheme : themeMode
  const theme = createTheme(actualTheme)

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme-mode', mode)
    }
  }

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        actualTheme,
        setThemeMode,
        theme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
